import styles from "./ReservationConfirmModalContent.module.scss"

import {
	FragmentType,
	getFragmentData,
	graphql,
} from "@/public/gql/__generated__"
import { toStringWithCommas } from "@/utils/strings/number"

import ClaimingConversionCancelPolicy from "../ClaimingConversionCancelPolicy/ClaimingConversionCancelPolicy"
import { lotteryPrizeText } from "../RereservationIncentiveBanner/lotteryPrize"
import { getMenuNameTexts } from "../RereservationPage.logic"
import LoadingOverlay from "../ReservableSlots/LoadingOverlay/LoadingOverlay"
import { originalStaffLabel, staffDisplayName } from "../freeStaff"

// LOTTERY 注意書きが使うフィールドは自分の fragment で宣言する (fragment collocation).
// ページは query で ...lotteryNoteSection を並列 spread し、masked ref をそのまま渡してくる.
// LOTTERY かどうか・倍率があるかの判定もこのコンポーネントに閉じる.
// export は Storybook の makeFragmentData 専用。useQuery / getFragmentData の引数として
// 他ファイルから使うのは禁止.
export const lotteryNoteSectionFragment = graphql(/* GraphQL */ `
	fragment lotteryNoteSection on RereservationIncentiveOutput {
		type
		price
		lotteryMultiplier
	}
`)

export type ReservationDetails = {
	datetime: string
	menuOrCouponNames: string[]
	duration: string
	originalStaffId: string
	// 前回予約が指名なしで、店舗の feature flag (HIDE_STAFF_FOR_NOT_NOMINATED_RERESERVATION) が ON のとき true。
	// true なら「指名スタッフ」行を出さない (指名していないのに【前回指名】と読ませないため)
	hidesStaffDueToNotNominated: boolean
	staff: {
		scrapingTargetSiteStaffId: string
		name: string
	}
	cancelConditionsTexts: string[]
	price: string
	// true の場合、「次回予約メニューは通常価格でのご案内になることがある」旨の注意書きを表示する
	// (nabeko の feature flag SHOW_NEXT_RESERVATION_MENU_REGULAR_PRICE_NOTE で店舗ごとに ON/OFF)
	showsNextReservationMenuRegularPriceNote: boolean
	// true の場合、キャンセルポリシー (キャンセル料率の一覧) を表示しない
	// (現在 nabeko は全店舗で true を返すため実質常に非表示。
	//  ただし useClaimingConversionRecancelPolicy が true ならこの値に関わらず表示する)
	hidesCancelPolicy: boolean
	// true の場合、請求転換 (前回キャンセル料を免除して再予約を案内した) の再予約なので、
	// 「本画面からの予約は請求の代わりなので、キャンセルされたらキャンセル料を請求する」特殊キャンセルポリシーを表示する
	useClaimingConversionRecancelPolicy: boolean
	// GIFT incentive (えらべるPay のポイント還元) 付きの予約なら還元ポイント数、
	// それ以外 (DISCOUNT / incentive なし) は null。
	// キャンセルポリシーを出さない時に、代わりにギフトの注意書きを出すかの判定に使う
	giftIncentivePoint: number | null
	// GIFT_CANDIDATE incentive (予算ありギフト) 付きの予約ならお渡しするポイント数、
	// それ以外は null。GIFT と同じ注意書きに「予算に達し次第終了」を足して出すかの判定に使う
	giftCandidatePoint: number | null
	// incentive の masked ref (LotteryNoteSection 用). incentive が無い予約では null.
	// LOTTERY 以外でも渡してよく、注意書きを出すかどうかは LotteryNoteSection 側が判定する
	incentive: FragmentType<typeof lotteryNoteSectionFragment> | null
}

// LOTTERY 予約の注意書き。この予約で抽選に参加するときの当選確率 (倍率) を出す。
// LOTTERY 以外・倍率なしでは何も出さない。
// メール登録の案内はここでは出さない (情報を絞るため。登録は予約確定後の完了画面で案内する)。
const LotteryNoteSection = ({
	incentive,
}: {
	incentive: FragmentType<typeof lotteryNoteSectionFragment>
}) => {
	const summary = getFragmentData(lotteryNoteSectionFragment, incentive)
	// サーバは LOTTERY なら必ず倍率を返す前提だが、万一 null なら注意書き自体を出さない.
	if (summary.type !== "LOTTERY" || summary.lotteryMultiplier === null) {
		return null
	}
	return (
		<div className={styles.lottery_note_section}>
			<div className={styles.lottery_note_header}>
				<span className={styles.lottery_badge}>キャンペーン適用中</span>
				<h4>{`抽選で${lotteryPrizeText(summary.price)}が当たる`}</h4>
			</div>
			<p>
				・ご来店いただくと当選確率
				<span className={styles.lottery_number}>
					{summary.lotteryMultiplier}
				</span>
				倍で抽選に参加できます
			</p>
		</div>
	)
}

type Props = {
	reservationDetails: ReservationDetails
	onConfirm: (selectedStaffId: string, staffName: string) => void
	onCancel: () => void
	isCreatingRereservation: boolean
}

const ReservationConfirmModalContent = ({
	reservationDetails,
	onConfirm,
	onCancel,
	isCreatingRereservation,
}: Props) => {
	// 請求転換の特殊キャンセルポリシーを出すか。カード・同意文・ボタン文言はこの値で揃えて出し分ける
	// (キャンセル料テキストが空でカードを出せないのに「同意して予約する」だけ出るのを防ぐ)
	const showsClaimingConversionPolicy =
		reservationDetails.useClaimingConversionRecancelPolicy &&
		reservationDetails.cancelConditionsTexts.length > 0

	// ギフトの注意書きに出すポイント数。
	// GIFT はキャンセルポリシーを出さない時だけ (空いた場所に出す)、
	// GIFT_CANDIDATE (予算ありギフト) は常に出す (「予算に達し次第終了」を予約前に必ず見せるため)
	let giftNotePoint: number | null = null
	if (reservationDetails.giftCandidatePoint !== null) {
		giftNotePoint = reservationDetails.giftCandidatePoint
	} else if (reservationDetails.hidesCancelPolicy) {
		giftNotePoint = reservationDetails.giftIncentivePoint
	}

	return (
		<div className={styles.container}>
			<button className={styles.close_button} onClick={onCancel}>
				×
			</button>

			<div className={styles.header}>
				<h3>以下の内容で予約を確定します</h3>
			</div>

			<table className={styles.menu_table}>
				<tbody>
					<tr className={styles.detail_item}>
						<td className={styles.detail_label}>ご予約日時</td>
						<td className={styles.detail_value}>
							{reservationDetails.datetime}
						</td>
					</tr>
					<tr className={styles.detail_item}>
						<td className={styles.detail_label}>施術時間</td>
						<td className={styles.detail_value}>
							{reservationDetails.duration}
						</td>
					</tr>
					<tr className={styles.detail_item}>
						<td className={styles.detail_label}>ご予約メニュー</td>
						<td className={styles.detail_value}>
							{getMenuNameTexts(reservationDetails.menuOrCouponNames).map(
								(menu, index) => (
									<div key={index}>{menu}</div>
								)
							)}
						</td>
					</tr>
					<tr className={styles.detail_item}>
						<td className={styles.detail_label}>ご予約金額</td>
						<td className={styles.detail_value}>{reservationDetails.price}</td>
					</tr>
					{!reservationDetails.hidesStaffDueToNotNominated && (
						<tr className={styles.detail_item}>
							<td className={styles.detail_label}>指名スタッフ</td>
							<td className={styles.detail_value}>
								{reservationDetails.originalStaffId ===
								reservationDetails.staff.scrapingTargetSiteStaffId
									? originalStaffLabel(
											reservationDetails.staff.scrapingTargetSiteStaffId,
											reservationDetails.staff.name
										)
									: staffDisplayName(
											reservationDetails.staff.scrapingTargetSiteStaffId,
											reservationDetails.staff.name
										)}
							</td>
						</tr>
					)}
				</tbody>
			</table>

			<div className={styles.note_wrapper}>
				{/* 指名スタッフ行を出さないとき (指名なし予約) は指名料の注記も出さない */}
				{!reservationDetails.hidesStaffDueToNotNominated && (
					<p className={styles.note_text}>
						※ 別途指名料がかかる場合がございます
					</p>
				)}
				<p className={styles.note_text}>
					※ 本画面の予約はホットペッパー予約ではありません
				</p>
				{reservationDetails.showsNextReservationMenuRegularPriceNote && (
					<p className={styles.note_text}>
						※ 次回予約メニューの場合、通常価格でのご案内となることがございます
					</p>
				)}
			</div>

			{reservationDetails.incentive !== null && (
				<LotteryNoteSection incentive={reservationDetails.incentive} />
			)}

			{/* キャンセルポリシーを出さない GIFT 予約では、空いた場所にギフトの注意書きを出す。
			    「当日のお会計が安くなる」「HPB のポイント」と誤解されるのを防ぐのが目的。
			    HPB のリクルートポイントと紛らわしいので「ポイント」ではなく「ギフト」と書く。
			    GIFT_CANDIDATE (予算ありギフト) はキャンセルポリシーの有無に関わらず同じ注意書きを出し、
			    末尾に「予算に達し次第終了」の 1 行を足す (biz 指示 2026-11-03) */}
			{giftNotePoint !== null && (
				<div className={styles.gift_note_section}>
					<div className={styles.gift_note_header}>
						<h4>キャンペーンについて</h4>
						<span className={styles.gift_badge}>キャンペーン適用中</span>
					</div>
					{/* 「ポイント」「ギフト」の語の途中で折り返されないよう nowrap で囲む */}
					<p>
						{`・${toStringWithCommas(giftNotePoint)}円相当の`}
						<span className={styles.nowrap}>ポイント</span>
						はお会計時の割引ではなく、
						<span className={styles.nowrap}>ギフト</span>
						でのお渡しです
					</p>
					<p>・ご来店を確認後、お送りします</p>
					{reservationDetails.giftCandidatePoint !== null && (
						<p>・予算に達し次第終了となります</p>
					)}
				</div>
			)}

			{/* 請求転換の再予約では、通常は非表示のキャンセルポリシーを特殊文面つきで表示する。
			    前回キャンセル料を免除しているので、再キャンセルは理由を問わず請求になることを予約前に明示する */}
			{showsClaimingConversionPolicy && (
				<div className={styles.claiming_conversion_policy_wrapper}>
					<ClaimingConversionCancelPolicy
						cancelConditionTexts={reservationDetails.cancelConditionsTexts}
					/>
				</div>
			)}

			{!reservationDetails.useClaimingConversionRecancelPolicy &&
				!reservationDetails.hidesCancelPolicy &&
				reservationDetails.cancelConditionsTexts.length > 0 && (
					<div className={styles.policy_section}>
						<h4>キャンセルポリシー</h4>
						{reservationDetails.cancelConditionsTexts.map((text, index) => (
							<p key={index}>{"・" + text}</p>
						))}
					</div>
				)}

			{showsClaimingConversionPolicy && (
				<p className={styles.consent_note}>
					「同意して予約する」を押すと、上記のキャンセルポリシーに同意したものとみなします
				</p>
			)}

			<div className={styles.footer}>
				<button
					type="button"
					className={styles.confirm_button}
					onClick={() =>
						onConfirm(
							reservationDetails.staff.scrapingTargetSiteStaffId,
							reservationDetails.staff.name
						)
					}
				>
					{showsClaimingConversionPolicy ? "同意して予約する" : "予約する"}
				</button>
			</div>
			<LoadingOverlay show={isCreatingRereservation} message="予約中です..." />
		</div>
	)
}

export default ReservationConfirmModalContent
